import { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useNavigate, useParams } from 'react-router-dom'
import { Language, stringToLanguage } from '.'
import { DwemerBootsI18nContext } from '../App'

// parseLanguagePath takes in lang which the URL param from router, currentLang from i18n,
// and curPath is from location.pathname. lang should always be a substring of curPath
// since it's being taken from the URL by router.
// parseLanguagePath will return a URL with a language if one is not set using the i18n language
// it will also make sure that the lang set in the URL is a valid one. If the i18n language
// needs to be changed because the URL lang is valid and different, change will be return as true
function parseLanguagePath(
  currentLang: Language,
  lang: string | undefined,
  curPath: string,
): { change: boolean; newPath: string } {
  let change = false
  let newPath = curPath
  if (lang) {
    const { lang: parsedLang, valid } = stringToLanguage(lang)
    if (valid) {
      if (parsedLang !== currentLang) {
        change = true
        newPath = curPath.replace(lang, parsedLang)
      }
    } else {
      if (!curPath.includes('/' + currentLang + '/')) {
        change = true
        newPath = curPath.replace(lang, currentLang)
      }
    }
  } else {
    change = true
    newPath = '/' + currentLang + curPath
  }
  return { change, newPath }
}

export default function LanguagePath() {
  const { i18n } = useTranslation()
  const { lang } = useParams()
  const dwemerBootsI18nContext = useContext(DwemerBootsI18nContext)
  const navigate = useNavigate()

  useEffect(() => {
    if (dwemerBootsI18nContext.changeLanguage) {
      const { change, newPath } = parseLanguagePath(
        i18n.language as Language,
        lang,
        location.pathname,
      )
      if (lang) {
        // change language if there's a language in the url that's different from the current language
        const { lang: parsedLang, valid } = stringToLanguage(lang)
        if (parsedLang !== (i18n.language as Language) && valid) {
          i18n.changeLanguage(parsedLang).catch((error) => {
            console.error('failed to change application i18n language:', error)
          })
          dwemerBootsI18nContext.changeLanguage(parsedLang).catch((error) => {
            console.error('failed to change dwemer boots i18n language:', error)
          })
        } else if (!valid) {
          const newPath = `/${i18n.language}${location.pathname}`
          navigate(newPath + window.location.search, { replace: false })
          return
        }
      }
      if (change) {
        navigate(`${newPath}${window.location.search}`, { replace: true })
      }
    }
  }, [lang, dwemerBootsI18nContext])

  return <Outlet />
}
